// Customers are *derived* on the client from data the admin already fetches —
// there is no dedicated /customers endpoint. We aggregate every booking and
// quote request into one person per unique contact (keyed by email, falling
// back to phone, then name) so the directory and per-customer history come for
// free without any backend change.

export interface RawContact {
  id?: string;
  name?: string;
  booking_name?: string;
  email_address?: string;
  phone_number?: string;
  booking_start_date_time?: string;
  status?: string;
  glam_package_id?: string;
  glam_spot?: string;
  number_of_faces?: number | string;
  [key: string]: any;
}

export interface DerivedBookingRef {
  id?: string;
  date?: string;
  status?: string;
  glam_package_id?: string;
  kind: "booking" | "quote";
}

export interface Customer {
  key: string;
  name: string;
  email: string;
  phone: string;
  bookingsCount: number;
  quotesCount: number;
  lastActivity: string | null;
  totalFaces: number;
  history: DerivedBookingRef[];
}

function contactName(c: RawContact): string {
  return (c.name || c.booking_name || "").trim();
}

function contactKey(c: RawContact): string {
  const email = (c.email_address || "").trim().toLowerCase();
  if (email) return `e:${email}`;
  const phone = (c.phone_number || "").replace(/\D/g, "");
  if (phone) return `p:${phone}`;
  const name = contactName(c).toLowerCase();
  return name ? `n:${name}` : "unknown";
}

/**
 * Fold a list of bookings and a list of quote requests into a de-duplicated
 * customer directory. Each customer carries a chronological history of their
 * bookings and quotes.
 */
export function deriveCustomers(
  bookings: RawContact[] = [],
  quotes: RawContact[] = []
): Customer[] {
  const map = new Map<string, Customer>();

  const ingest = (c: RawContact, kind: "booking" | "quote") => {
    const key = contactKey(c);
    if (key === "unknown") return;
    const date = c.booking_start_date_time ?? null;
    const faces = Number(c.number_of_faces) || 0;

    let entry = map.get(key);
    if (!entry) {
      entry = {
        key,
        name: contactName(c) || c.email_address || c.phone_number || "Unknown",
        email: (c.email_address || "").trim(),
        phone: (c.phone_number || "").trim(),
        bookingsCount: 0,
        quotesCount: 0,
        lastActivity: null,
        totalFaces: 0,
        history: [],
      };
      map.set(key, entry);
    }

    // Prefer a real name / contact detail if a later record has one.
    if (!entry.name || entry.name === "Unknown") entry.name = contactName(c) || entry.name;
    if (!entry.email && c.email_address) entry.email = c.email_address.trim();
    if (!entry.phone && c.phone_number) entry.phone = c.phone_number.trim();

    if (kind === "booking") entry.bookingsCount += 1;
    else entry.quotesCount += 1;

    entry.totalFaces += faces;
    if (date && (!entry.lastActivity || date > entry.lastActivity)) {
      entry.lastActivity = date;
    }
    entry.history.push({
      id: c.id,
      date: date ?? undefined,
      status: c.status,
      glam_package_id: c.glam_package_id,
      kind,
    });
  };

  bookings.forEach((b) => ingest(b, "booking"));
  quotes.forEach((q) => ingest(q, "quote"));

  const list = Array.from(map.values());
  list.forEach((c) =>
    c.history.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
  );
  // Most recently active first.
  list.sort((a, b) => (b.lastActivity ?? "").localeCompare(a.lastActivity ?? ""));
  return list;
}
